import React, { FC, useCallback, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  fetchProducts,
  resetSearchList,
  getProductsList,
  getProductsPagination,
  getLoaders
} from 'features/Products/products';
import { Product } from 'features/Products/productsTypes';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableRow from '@material-ui/core/TableRow';
import { PageIndex } from 'components/UI/PageIndex';
import { Row } from 'components/UI/Row';
import { Column } from 'components/UI/Column';
import { TableLoader } from 'components/Loaders/TableLoader';
import { mainColor } from 'theme/Theme';

const SelectableProducts: FC<{
  productsInOrderlist: string[];
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product | null) => void;
}> = ({ productsInOrderlist, selectedProduct, setSelectedProduct }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const products = useSelector(getProductsList);
  const { isLoading } = useSelector(getLoaders);
  const pagination = useSelector(getProductsPagination);
  const tableRef = useRef<HTMLTableElement>(null);

  useEffect(() => {
    dispatch(fetchProducts({ index: 1 }));
    return () => {
      dispatch(resetSearchList());
    };
  }, []);

  const isSelected = useCallback((sn: string) => productsInOrderlist.find((e: string) => e === sn), [
    productsInOrderlist
  ]);

  return (
    <>
      <div style={{ marginTop: '20px', marginBottom: selectedProduct ? '20px' : 0 }}>
        <strong>Products</strong>
      </div>
      <Accordion expanded={!selectedProduct}>
        <AccordionSummary
          expandIcon={selectedProduct && <ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          onClick={() => setSelectedProduct(null)}
          style={{
            borderBottom: !selectedProduct ? 'solid 1px lightgrey' : '',
            cursor: !selectedProduct ? 'default' : 'pointer'
          }}
        >
          {selectedProduct?.name
            ? t('orderList.product selected', { productName: selectedProduct?.name })
            : t('orderList.no product selected')}
        </AccordionSummary>
        <AccordionDetails style={{ padding: 0 }}>
          <Column>
            <TableContainer component={Paper} elevation={3} style={{ height: '40vh' }}>
              <Table stickyHeader aria-label="order list" ref={tableRef}>
                <TableBody>
                  {isLoading && <TableLoader colSpan={1} />}
                  {!isLoading && isEmpty(products) && (
                    <TableRow>
                      <TableCell>
                        <Row height="40vh" justifyContent="center" alignItems="center">
                          {t('products.no product found')}
                        </Row>
                      </TableCell>
                    </TableRow>
                  )}
                  {products.map((p: any) => (
                    <TableRow
                      key={`product_${p.id}`}
                      hover
                      style={{
                        cursor: 'pointer',
                        backgroundColor: isSelected(p.id) ? mainColor : ''
                      }}
                      onClick={() => {
                        setSelectedProduct(p);
                      }}
                    >
                      <TableCell style={{ color: isSelected(p.id) ? 'white' : 'black' }}>{p.name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <PageIndex
              margin="25px 0"
              justifyContent="center"
              index={pagination.index}
              indexesCount={pagination.indexesCount || 0}
              onChange={async (_, page: number) => {
                await dispatch(fetchProducts({ index: page } /* , pagination.searchTerm */));
                if (tableRef) {
                  tableRef?.current?.scrollIntoView();
                  window.scrollTo({ top: 0 });
                }
              }}
            />
          </Column>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export { SelectableProducts };
